import {Component, HostListener, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {PushNotifications} from '@capacitor/push-notifications';
import {defer} from 'lodash';
import AuthApi from 'src/app/domain/service/api/auth.api';
import {Device} from 'src/app/infrastructure/device/device';
import {InactivityHandler} from 'src/app/infrastructure/device/inactivity-handler';
import {AppEnvironmentService} from 'src/app/infrastructure/environment/app-environment/app-environment.service';
import {UserSession} from 'src/app/infrastructure/environment/user-session';
import {CustomerIoService} from 'src/app/infrastructure/analytics/customer-io.service';
import {GtmService} from 'src/app/infrastructure/analytics/gtm.service';
import {PendingNavigationService} from 'src/app/infrastructure/service/pending-navigation.service';
import {Bootstrap} from 'src/app/infrastructure/bootstrap/bootstrap';
import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-root',
  template: `
    <ion-app>
      <app-loader-overplay></app-loader-overplay>
      <ion-router-outlet></ion-router-outlet>
    </ion-app>
  `,
  styleUrls: ['./webapp/page/web.page.scss']
})
export class AppComponent implements OnInit {
  private isApp: boolean;

  constructor(
    private readonly bootstrap: Bootstrap,
    private readonly inactivityHandler: InactivityHandler,
    private router: Router,
    private authApi: AuthApi,
    private appEnvironment: AppEnvironmentService,
    private userSession: UserSession,
    private customerIoService: CustomerIoService,
    private gtmService: GtmService,
    private pendingNavigationService: PendingNavigationService,
    device: Device
  ) {
    this.isApp = device.isApp();
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event) {
    if (this.isApp) {
      this.inactivityHandler.notifyUserInteraction();
    }
  };

  @HostListener('click', ['$event'])
  onClick(event) {

    if (!this.isApp) {
      this.inactivityHandler.notifyUserInteraction();
    }
  };

  @HostListener('window:beforeunload', ['$event'])
  async beforeunloadHandler(event: Event) {
    /*
     * This code performs a logout when closing the tab,
     * in case the login in the application is done externally
     * (for example, with EVO), which will destroy the user's information.
     * This is done to avoid subsequent issues with the HttpInterceptor
     * when performing the next login from the external platform of origin.
     */
    if (this.appEnvironment.userSessionParameters().closeSessionOnEveryPageReload) {
      this.authApi.logout();
      this.inactivityHandler.notifyLogout();
    }
  }

  ngOnInit(): void {
    try {
      this.gtmService.loadGTM();
    } catch (e) {
      console.error('Error loading GTM', e);
    }
    this.bootstrap.preloadStoredSession();

    defer(async () => {
      await this.bootstrap.bootstrap();
    });

    this.initPushNotificationActionListener();
  }

  private initPushNotificationActionListener(): void {
    if (!this.isApp) {
      return;
    }

    PushNotifications.addListener('pushNotificationActionPerformed', async action => {
      console.log('Notification action performed', action);
      const deliveryId = action.notification.data?.['CIO-Delivery-ID'];
      if (deliveryId) {
        const siteId = environment.analytics.customerIoSiteId;
        const userId = this.userSession?.loggedUser()?.id;
        if (siteId && userId) {
          this.customerIoService.addCustomerIoScript(siteId, userId, {});
          this.customerIoService.trackPushOpened(deliveryId);
        }
      }
      const route = action.notification.data?.route;
      if (route) {
        if (this.userSession.isLogged()) {
          this.router.navigateByUrl(route);
        } else {
          this.pendingNavigationService.setPendingRoute(route);
        }
      }
    });
  }
}
